/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Button, Grid, Skeleton, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "@store/store";
import { useLazyGetPersonsQuery } from "@api/personasApi";
import { useNavigate } from "react-router-dom";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { InactivityHandler } from "@components/InactivityHandler";
import { ApiError, PersonaStructure } from "@interfaces/response";
import CustomRightImage from "@components/CustomRightImagev2";
import * as Sentry from "@sentry/react";
import IconRejected from "@components/Images/IconRejected";
import { setSelectedAval } from "@store/slices/segundoAvalSlice";

const SEGUNDO_AVAL_CODE = "PTSAV";

const AvalesAdicionales = () => {
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const applicationData = useSelector((state: RootState) => state.register.application);

  const [getPersons] = useLazyGetPersonsQuery();

  const [isLoading, setIsLoading] = useState(false);
  const [avales, setAvales] = useState<PersonaStructure[]>([]);

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);

        const res_persons = await getPersons({
          loanApplicationId: applicationData.id,
          personTypeCode: [SEGUNDO_AVAL_CODE],
        }).unwrap();

        setAvales(res_persons);
      } catch (error) {
        const api_error = error as ApiError;
        Sentry.captureException(error, {
          extra: {
            component: "AvalesAdicionales-initial_fetch",
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  const handleFillAval = (aval: PersonaStructure) => {
    dispatch(setSelectedAval(aval));
    navigate(`/producto/creditosimple/llenado_datos_segundo_aval`);
  };

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8}>
        <Box maxWidth="sm" mx="auto" mt={4} px={2}>
          <Typography
            textAlign="center"
            variant={isMobileOrTablet ? "body2" : "body1"}
            fontWeight={700}
            color={theme?.palette?.primary?.dark}
          >
            Solicitud de Aval Adicional
          </Typography>
          <InactivityHandler />
          <Typography
            mt={2}
            textAlign="center"
            variant={isMobileOrTablet ? "body2" : "body1"}
            color={theme?.palette?.primary?.main}
            fontWeight={600}
          >
            Te solicitamos ingresar la información de <br />
            los avales adicionales solicitados.
          </Typography>

          <Box mt={6}>
            {isLoading ? (
              <>
                <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 2, mt: 4 }} />
                <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 2, mt: 2 }} />
                <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 2, mt: 2 }} />
              </>
            ) : avales.length > 0 ? (
              <>
                {avales.map((aval, index) => (
                  <Box key={aval.id} mt={4} display="flex" justifyContent="space-between" alignItems="center">
                    <Typography
                      variant="body1"
                      sx={{ flex: 1, textAlign: "left" }}
                      color={theme?.palette?.primary?.dark}
                    >
                      Información aval Adicional {index === 0 ? "" : index + 1}
                      <br />
                      <small style={{ color: theme?.palette?.primary?.main }}>
                        {aval.name} {aval.last_name} {aval.last_name_2}
                      </small>
                    </Typography>

                    <Button
                      variant="default"
                      sx={{ width: "100px", padding: "0px" }}
                      onClick={() => handleFillAval(aval)}
                    >
                      Llenar
                    </Button>
                  </Box>
                ))}

                <Typography
                  mt={4}
                  textAlign="center"
                  variant="body2"
                  color={theme?.palette?.grey?.[600]}
                  sx={{ fontStyle: "italic" }}
                >
                  Una vez completada la información de todos los avales adicionales, podrás continuar con el proceso.
                </Typography>
              </>
            ) : (
              <Box display="flex" flexDirection="column" alignItems="center" mt={4}>
                <IconRejected width={isMobileOrTablet ? "110" : "170"} fill={theme.palette.primary.light} />
                <Typography
                  mt={2}
                  textAlign="center"
                  variant={isMobileOrTablet ? "body2" : "body1"}
                  color={theme?.palette?.primary?.dark}
                >
                  La solicitud no cuenta con avales adicionales
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Grid>

      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};

export default AvalesAdicionales;
